<script setup lang="ts">
import { ref, provide, nextTick, watch, computed, onMounted } from 'vue';
import { useI18N } from '@dialpad/i18n';
import { useRouter } from 'vue-router';
import { useCampaignStore, useLeadListStore } from '@/stores';
import { DtInput } from '@dialpad/dialtone/vue3/lib/input';
import { DtModal } from '@dialpad/dialtone/vue3/lib/modal';
import { updateShow } from '@/app/components/modal/modals';
import type { ModalProps } from '@/app/components/modal/modals';
import CallCenterSelect from '@/call-center/components/CallCenterSelect/CallCenterSelect.vue';
import CallerIdSelect from '../../../call-center/components/CallerIdSelect/CallerIdSelect.vue';
import { DtButton } from '@dialpad/dialtone/vue3/lib/button';
import { TRIGGER_FORM_VALIDATION } from '@/app/utils/keys';
import type { CallCenter, DidDetail } from '@/apis/generated/dialpad-contact';
import { DATA_QA } from './create-campaign.constants';
import { DATA_QA as MODAL_WRAPPER_DATA_QA } from '@/app/components/modal/modal-wrapper.constants';
import logger from '@/logger';
import { roundToNearestHours, addHours } from 'date-fns';
import type { Campaign } from '@/apis/generated/dialbunny/models/Campaign';

const log = logger.getChild('CreateCampaign');
log.debug('Mounting');
const props = defineProps<ModalProps>();

const { $t } = useI18N();
const router = useRouter();
const show = ref(false);

onMounted(() => {
  log.debug('Mounted');
  show.value = true;
});

const campaignStore = useCampaignStore();
const leadListStore = useLeadListStore();

const campaignNameRaw = ref<string>('');
const callCenter = ref<CallCenter | null>(null);
const didDetail = ref<DidDetail | null>(null);
const validationTrigger = ref(0);
const campaignNameMessages = ref<{ type: 'error'; message: string }[]>([]);
const creating = ref(false);

provide(TRIGGER_FORM_VALIDATION, validationTrigger);

const campaignName = computed(() => campaignNameRaw.value.trim());

const didsDetails = computed(() => {
  return callCenter.value?.didsDetails ?? [];
});

watch(campaignName, (name, prev) => {
  if (name.length > 0 && prev.length === 0) {
    log.debug('Campaign name changed to non-empty');
    campaignNameMessages.value = [];
  }
});

watch(callCenter, (newCallCenter) => {
  log.debug('Call center changed', newCallCenter);
  didDetail.value = newCallCenter?.didsDetails?.length
    ? newCallCenter.didsDetails[0]
    : null;
});

const validate = async () => {
  log.debug('Validating');
  validationTrigger.value++;
  await nextTick();
  if (
    campaignName.value.length === 0 &&
    campaignNameMessages.value.length === 0
  ) {
    log.debug('Name is empty');
    campaignNameMessages.value.push({
      type: 'error',
      message: $t('CREATE_CAMPAIGN_CAMPAIGN_NAME_ERROR_MESSAGE'),
    });
  }

  const result =
    campaignName.value.length > 0 &&
    callCenter.value !== null &&
    didDetail.value !== null;

  log.debug('Validation result', result);

  return result;
};

const updateLeadList = async (campaign: Campaign) => {
  return new Promise((resolve, reject) => {
    if (!campaign.leadListId) {
      log.error('Campaign leadListId is missing', campaign);
      // TODO: show banner
      reject(new Error('Campaign leadListId is missing'));
      return;
    }
    leadListStore.update(
      campaign.leadListId,
      {
        csvIds: [props.meta?.csvId],
      },
      {
        onSuccess: resolve,
        onRetry: () => {
          // show toast
        },
        onError: (error) => {
          log.log(
            `Error updating lead list ${campaign.leadListId} with csv ${props.meta?.csvId}`,
            error,
          );
          reject(error);
        },
      },
    );
  });
};

// eslint-disable-next-line max-statements
const continueToEdit = async () => {
  log.debug('Continue to edit');
  if (!(await validate())) {
    return;
  }
  if (!callCenter.value?.groupId) {
    throw new Error('Call center groupId is missing');
  }
  if (!didDetail.value?.number) {
    throw new Error('DID detail number is missing');
  }
  log.debug(
    `Creating campaign with groupId: ${callCenter.value.groupId}, callerId: ${didDetail.value.number}, name: ${campaignName.value}`,
  );
  creating.value = true;
  campaignStore.create(
    {
      name: campaignName.value,
      groupId: callCenter.value.groupId,
      callerId: didDetail.value.number,
      dateScheduledEnd: roundToNearestHours(addHours(new Date(), 24), {
        roundingMethod: 'floor',
      }),
      autoCreate: true,
      setTimegate: true,
    },
    {
      onSuccess: async (campaign) => {
        log.debug('Campaign created', campaign);
        if (props.meta?.csvId) {
          await updateLeadList(campaign);
        }
        creating.value = false;
        void router.push({ name: 'campaign', params: { id: campaign.id } });
      },
      onRetry: () => {
        // show toast
      },
      onError: () => {
        creating.value = false;
        // show toast
      },
    },
  );
};
</script>
<template>
  <DtModal
    :data-qa="MODAL_WRAPPER_DATA_QA.DIALOG"
    :title="$t('CREATE_CAMPAIGN_MODAL_TITLE')"
    :close-button-props="{
      ariaLabel: $t('MODAL_CLOSE_ARIA_LABEL'),
      'data-qa': MODAL_WRAPPER_DATA_QA.CLOSE,
    }"
    :show="show"
    content-class="d-p4 d-mn4"
    @update:show="updateShow"
  >
    <div :data-qa="DATA_QA.NAME">
      <DtInput
        v-model="campaignNameRaw"
        :data-qa="DATA_QA.NAME_INPUT"
        class="d-to-ellipsis"
        :label="$t('CREATE_CAMPAIGN_CAMPAIGN_NAME_LABEL')"
        :placeholder="$t('CREATE_CAMPAIGN_CAMPAIGN_NAME_PLACEHOLDER')"
        :messages="campaignNameMessages"
      />
    </div>
    <CallCenterSelect
      v-model="callCenter"
      class="d-mt24"
      :required="true"
      :label="$t('CREATE_CAMPAIGN_CONTACT_CENTER_LABEL')"
    />
    <CallerIdSelect
      v-if="didsDetails.length > 0"
      v-model="didDetail"
      class="d-mt8"
      :required="true"
      :dids-details="didsDetails"
      :label="$t('CREATE_CAMPAIGN_CALLER_ID_LABEL')"
    />
    <div class="d-d-flex d-jc-flex-end d-mt16">
      <DtButton
        :data-qa="DATA_QA.CONTINUE"
        :loading="creating"
        @click="continueToEdit"
      >
        {{ $t('CREATE_CAMPAIGN_CONTINUE') }}
      </DtButton>
    </div>
  </DtModal>
</template>
